import { useEffect, useState } from 'react';
import { X, ClipboardList } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import type { Asset, Channel } from '../../types/assetRegistry';

interface LogUsageModalProps { asset: Asset; onClose: () => void; onLogged: () => void; }

const inputBase: React.CSSProperties = {
  width: '100%', padding: '9px 12px', fontSize: 13, border: '1.5px solid #e5e7eb',
  borderRadius: 8, outline: 'none', background: '#fff', color: '#111827',
  fontFamily: 'inherit', transition: 'border-color 0.15s', boxSizing: 'border-box',
};
const fieldLabel: React.CSSProperties = { display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 6 };

export function LogUsageModal({ asset, onClose, onLogged }: LogUsageModalProps) {
  const [channels, setChannels] = useState<Channel[]>([]);
  const [channelId, setChannelId] = useState('');
  const [context, setContext] = useState('');
  const [kranumCampaignId, setKranumCampaignId] = useState('');
  const [usedAt, setUsedAt] = useState(new Date().toISOString().split('T')[0]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    supabase.from('channels').select('*').order('name').then(({ data }) => {
      if (data) setChannels(data as Channel[]);
    });
  }, []);

  const focusStyle = (e: React.FocusEvent<any>) => (e.target.style.borderColor = '#1B4B8A');
  const blurStyle  = (e: React.FocusEvent<any>) => (e.target.style.borderColor = '#e5e7eb');

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!channelId) { setError('Please select a channel.'); return; }
    setSaving(true); setError('');
    const { error: insertError } = await supabase.from('usage_log').insert({
      asset_id: asset.id, channel_id: channelId,
      context: context.trim() || null,
      kranum_campaign_id: kranumCampaignId.trim() || null,
      used_at: new Date(usedAt).toISOString(),
    });
    if (insertError) { setError(insertError.message); setSaving(false); return; }
    setSaving(false); onLogged();
  }

  return (
    <div className="asset-registry-scope" style={{ position: 'fixed', inset: 0, zIndex: 60, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(4px)' }} onClick={onClose} />
      <div style={{ position: 'relative', background: '#fff', borderRadius: 16, boxShadow: '0 20px 60px rgba(0,0,0,0.2)', width: 460, overflow: 'hidden' }}>

        <style>{`.ar-panel-header, .ar-panel-header *, .ar-panel-header h2, .ar-panel-header div, .ar-panel-header button { color: #ffffff !important; }`}</style>
        {/* Header */}
        <div className="ar-panel-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 24px', background: '#0f2240' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <ClipboardList size={16} color="rgba(255,255,255,0.7)" />
            <h2 style={{ margin: 0, fontSize: 15, fontWeight: 600, color: '#fff' }}>Log Usage</h2>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'rgba(255,255,255,0.6)', display: 'flex', padding: 4, borderRadius: 6 }}>
            <X size={18} />
          </button>
        </div>

        <form onSubmit={handleSubmit} style={{ padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: 16 }}>
          {/* Asset name */}
          <div style={{ background: '#f9fafb', borderRadius: 8, padding: '10px 14px', border: '1px solid #e5e7eb' }}>
            <p style={{ margin: '0 0 2px', fontSize: 11, color: '#9ca3af', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Asset</p>
            <p style={{ margin: 0, fontSize: 14, fontWeight: 600, color: '#111827' }}>{asset.title}</p>
          </div>

          {error && (
            <div style={{ background: '#fef2f2', border: '1px solid #fecaca', color: '#dc2626', fontSize: 13, padding: '10px 14px', borderRadius: 8 }}>{error}</div>
          )}

          <div>
            <label style={fieldLabel}>Channel <span style={{ color: '#ef4444' }}>*</span></label>
            <select value={channelId} onChange={e => setChannelId(e.target.value)}
              style={{ ...inputBase, cursor: 'pointer' }} onFocus={focusStyle} onBlur={blurStyle}>
              <option value="">Select channel...</option>
              {channels.map(c => <option key={c.id} value={c.id}>{c.name.replace(/_/g, ' ')}</option>)}
            </select>
          </div>

          <div>
            <label style={fieldLabel}>Context</label>
            <input type="text" value={context} onChange={e => setContext(e.target.value)}
              placeholder="e.g. Carepreneur March 2026 page 3"
              style={inputBase} onFocus={focusStyle} onBlur={blurStyle} />
          </div>

          <div>
            <label style={fieldLabel}>Kranum Campaign ID <span style={{ color: '#9ca3af', fontWeight: 400 }}>(optional)</span></label>
            <input type="text" value={kranumCampaignId} onChange={e => setKranumCampaignId(e.target.value)}
              placeholder="Leave blank for now"
              style={inputBase} onFocus={focusStyle} onBlur={blurStyle} />
          </div>

          <div>
            <label style={fieldLabel}>Date</label>
            <input type="date" value={usedAt} onChange={e => setUsedAt(e.target.value)}
              style={inputBase} onFocus={focusStyle} onBlur={blurStyle} />
          </div>

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, paddingTop: 4 }}>
            <button type="button" onClick={onClose} style={{ padding: '9px 18px', fontSize: 13, fontWeight: 600, color: '#374151', border: '1.5px solid #e5e7eb', borderRadius: 8, background: '#fff', cursor: 'pointer' }}>
              Cancel
            </button>
            <button type="submit" disabled={saving} style={{
              display: 'flex', alignItems: 'center', gap: 6, padding: '9px 20px', fontSize: 13, fontWeight: 600,
              color: '#fff', background: saving ? '#93a8c4' : '#1B4B8A', border: 'none', borderRadius: 8, cursor: saving ? 'not-allowed' : 'pointer',
            }}>
              <ClipboardList size={14} />{saving ? 'Logging...' : 'Log Usage'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
